import React, { useRef, useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../services/api';
import '../styles/PreferentialProcurementDocumentation.scss';

const DownloadIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
    <polyline points="7 10 12 15 17 10"/>
    <line x1="12" y1="15" x2="12" y2="3"/>
  </svg>
);

const UploadIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
    <polyline points="17 8 12 3 7 8"/>
    <line x1="12" y1="3" x2="12" y2="15"/>
  </svg>
);

// combined: true -> one upload calculates every id in `elements` via calculate_esd
// combined: false -> the single id in `elements` is calculated via the plain calculate action

// Per-element display variant data. Adding a new sector's ED * SD variant means adding one entry here, not a new page file.
const VARIANTS = {
  enterprise_supplier_development: {
    combined: true,
    elements: ['enterprise_development', 'supplier_development'],
    downloadElement: 'enterprise_development',
    pageDesc: 'Enterprise Development and Supplier Development - Upload and Calculate Scorecard',
    instructionText: 'Download the template, fill it in, then upload to calculate both ED and SD scorecards',
    templateFilename: 'EDDataSheetTemplate.xlsx',
    cardDesc: 'ED & SD Data Sheet (.xlsx)',
    backPath: '/esd/enterprise-supplier-development',
    scorecardPath: 'esd/enterprise-supplier-development/scorecard',
  },
  enterprise_development_transport: {
    combined: false,
    elements: ['enterprise_development_transport'],
    downloadElement: 'enterprise_development_transport',
    pageDesc: 'Enterprise Development - Upload and Calculate Scorecard',
    instructionText: 'Download the template, fill it in, then upload to calculate your Enterprise Development scorecard',
    templateFilename: 'ED Data Sheet Template Road Freight.xlsx',
    cardDesc: 'ED Data Sheet Template Road Freight(.xlsx)',
    backPath: '/esd/enterprise-supplier-development',
    scorecardPath: '/esd/enterprise-supplier-development/scorecard/enterprise-development-transport',
  },
};

const emptyResult = (elementId) => ({ elementId, label: null, total_points: 0, bonus_selected: false, filename: null, indicators: [] });
const fmt = (n) => (Math.round(n * 100) / 100).toFixed(2);

const ESDSubmitDataSheet = () => {
  const navigate = useNavigate();
  const { elementKey: rawElementKey } = useParams();
  const elementKey = (rawElementKey || 'enterprise_supplier_development').replace(/-/g, '_');
  const variant = VARIANTS[elementKey] || VARIANTS.enterprise_supplier_development;

  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [results, setResults] = useState(() => variant.elements.map(emptyResult));
  const [error, setError] = useState(null);

  const updateResult = (elementId, patch) => {
    setResults(list => list.map(r => (r.elementId === elementId ? {...r, ...patch } : r)));
  };

  useEffect(() => {
    setResults(variant.elements.map(emptyResult));
    variant.elements.forEach(elementId => {
      api.get(`/core/scorecard/get_scorecard/?element=${elementId}`)
        .then(res => updateResult(elementId, {
          label: res.data.label ?? null,
          total_points: res.data.total_points,
          bonus_selected: res.data.bonus_selected ?? false,
          filename: res.data.filename,
          indicators: res.data.indicators || [],
        }))
        .catch(() => {});
    });
  }, [elementKey]);   //eslint-disable-line react-hooks/exhaustive-deps
  
  const handleDownloadTemplate = async () => {
    try {
      const res = await api.get(`/core/scorecard/download_template/?element=${variant.downloadElement}`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = variant.templateFilename;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setError('Template not found on server.');
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', file);
    if (variant.combined) {
      const res = await api.post('/core/scorecard/calculate_esd/', formData);
      updateResult('enterprise_development', { ...res.data.ed, filename: res.data.filename });
      updateResult('supplier_development', { ...res.data.sd, filename: res.data.filename });
    } else {
      const elementId = variant.elements[0];
      formData.append('element', elementId);
      const res = await api.post('/core/scorecard/calculate/', formData);
      updateResult(elementId, res.data);
    }
    } catch (err) {
      if (err.response?.status === 422) {
        setError(err.response?.data?.error || 'Missing required data. Check Company Information.');
      } else {
        setError(err.response?.data?.error || 'Failed to process the data sheet. Check the file format and try again.');
      }
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const hasResult = results.some(r => r.filename);
  const uploadFilename = results.find(r => r.filename)?.filename;

  return (
    <div className="od-container">
      <div className="od-header">
        <button className="od-back" onClick={() => navigate(variant.backPath)}>← Back</button>
        <div>
          <h1>Submit Data Sheet</h1>
          <p>{variant.pageDesc}</p>
        </div>
      </div>

      <div className="od-section-label">
        <p>{variant.instructionText}</p>
      </div>

      <div className="od-grid">
        <button className="od-card" onClick={handleDownloadTemplate}>
          <div className="od-card-icon"><DownloadIcon /></div>
          <span className="od-card-label">Download Template</span>
          <span className="od-card-desc">{variant.cardDesc}</span>
        </button>

        <button
          className={`od-card ${hasResult ? 'od-card--uploaded' : ''}`}
          onClick={() => fileInputRef.current.click()}
          disabled={uploading}
        >
          <div className="od-card-icon"><UploadIcon /></div>
          <span className="od-card-label">
            {uploading ? 'Calculating…' : 'Upload Completed Sheet'}
          </span>
          {uploadFilename && (
            <span className="od-card-status">✓ {uploadFilename}</span>
          )}
        </button>
      </div>

      <input
        type="file"
        ref={fileInputRef}
        accept=".xlsx"
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />

      {error && (
        <div className="od-error">
          {error}
          {error.includes('NPAT') && (
            <button className="od-error-link" onClick={() => navigate('/finance-statutory/npat')}>
              Update NPAT
            </button>
          )}
        </div>
      )}

      {hasResult && (
        <div className="od-results-grid">
          {results.map(r => {
            const maxPoints = (r.indicators || []).reduce((s, ind) => s + (ind.points || 0), 0)
            return (
            <div className="od-result" key={r.elementId}>
              <div className="od-result-header">
                <strong>{r.label || r.elementId}</strong>
                {r.filename && <span>File: {r.filename}</span>}
              </div>
              <div className="od-result-score">
                <span className="od-result-points">{fmt(r.total_points + (r.bonus_selected ? 1 : 0))}</span>
                <span className="od-result-max">/ {maxPoints || '-'} points</span>
              </div>
            </div>
            );
          })}
          <button
            className="od-result-btn od-result-btn--full"
            onClick={() => navigate(variant.scorecardPath)}
          >
            View Full Scorecard →
          </button>
        </div>
      )}
    </div>
  );
};

export default ESDSubmitDataSheet;
